import {
  currentCharterParty,
  type OwnerReference,
  ownerReferenceOf,
  type PerformanceReference,
  performanceReference,
  type VoyageCpBasis,
  voyageCharterParty,
} from '@felix/domain';
import type { CharterPartyItem, CharterPartyRegisterData, Voyage } from './types.js';

/**
 * Seferin performans referansı ekranda (ADR 0084 Ek 2): sunucunun `voyageReferenceOf`'u ile aynı
 * kural (CP > Owner's reference > yok). Sunucunun hesabı (`performance.reference`) varsa o
 * kullanılır; form gibi kaydedilmemiş durumlarda bu fonksiyon. Saf fonksiyonlar.
 */

export type ReferenceBasis = VoyageCpBasis | 'legacy' | null;

export interface ReferenceView {
  reference: PerformanceReference;
  /** Seferin CP'si (register'daki kayıt); eski seferin kolonlarından gelen CP'de `null`. */
  charterParty: CharterPartyItem | null;
  basis: ReferenceBasis;
}

type VoyageFields = Pick<Voyage, 'id'> &
  Partial<
    Pick<
      Voyage,
      | 'charterPartyId'
      | 'ownerReference'
      | 'start'
      | 'plannedEtd'
      | 'charterer'
      | 'charterPartyNo'
      | 'cpSpeedLadenKn'
      | 'cpSpeedBallastKn'
      | 'cpConsumptionLadenMt'
      | 'cpConsumptionBallastMt'
    >
  >;

export function voyageReference(
  voyage: VoyageFields,
  register: Pick<CharterPartyRegisterData, 'items' | 'ownerReference'> | null,
): ReferenceView {
  const items = register?.items ?? [];
  const found = voyageCharterParty(
    {
      id: voyage.id,
      charterPartyId: voyage.charterPartyId ?? null,
      start: voyage.start ?? null,
      plannedEtd: voyage.plannedEtd ?? null,
    },
    items,
  );
  const legacy = [
    voyage.cpSpeedLadenKn,
    voyage.cpSpeedBallastKn,
    voyage.cpConsumptionLadenMt,
    voyage.cpConsumptionBallastMt,
  ].some((v) => typeof v === 'number');
  const legacyCp =
    !found && legacy
      ? {
          id: null,
          cpNo: voyage.charterPartyNo ?? null,
          charterer: voyage.charterer ?? null,
          terms: {
            speedLadenKn: voyage.cpSpeedLadenKn ?? null,
            speedBallastKn: voyage.cpSpeedBallastKn ?? null,
            consumptionLadenMt: voyage.cpConsumptionLadenMt ?? null,
            consumptionBallastMt: voyage.cpConsumptionBallastMt ?? null,
          },
        }
      : null;
  return {
    reference: performanceReference({
      charterParty: found?.item ?? legacyCp,
      voyageOwner: ownerReferenceOf(voyage.ownerReference ?? null),
      vesselOwner: ownerReferenceOf(register?.ownerReference ?? null),
    }),
    charterParty: found?.item ?? null,
    basis: found ? found.basis : legacyCp ? 'legacy' : null,
  };
}

/** Yeni seferin önerilen CP'si: planlanan kalkışı (yoksa şimdiyi) kapsayan CP dönemi. */
export function suggestedCharterParty(
  items: readonly CharterPartyItem[],
  plannedEtd: string | null,
  now: Date,
): CharterPartyItem | null {
  return currentCharterParty(items, plannedEtd ?? now.toISOString());
}

/** Seçim listesindeki CP: "CP no — kiracı" (biri yoksa öbürü). */
export function charterPartyOption(item: Pick<CharterPartyItem, 'cpNo' | 'charterer'>): string {
  return [item.cpNo, item.charterer].filter(Boolean).join(' — ') || '—';
}

/** Formdaki sahibin referansı: hepsi boşsa `null` (gönderilmez). */
export function ownerReferenceInput(
  values: Partial<Record<keyof OwnerReference, number | null>>,
): OwnerReference | null {
  return ownerReferenceOf({
    speedLadenKn: values.speedLadenKn ?? null,
    speedBallastKn: values.speedBallastKn ?? null,
    consumptionLadenMt: values.consumptionLadenMt ?? null,
    consumptionBallastMt: values.consumptionBallastMt ?? null,
  });
}
